import type { Session } from "next-auth";
import { normalizeCompanyMemberRole } from "@/lib/company-member-roles";

export const CREW_HOME_PATH = "/app/crew";
export const OFFICE_HOME_PATH = "/app";
/** Post-login chooser for staff who can use both office and field. */
export const WORKSPACE_CHOOSER_PATH = "/app/enter";

type CrewUserLike = {
  companyId?: string | null;
  role?: string | null;
  scafferAccountKind?: string | null;
};

/** Field crew on a company team (not office/admin/owner; not independent Scaff Pro). */
export function isCompanyCrewUser(user: CrewUserLike | null | undefined): boolean {
  if (!user?.companyId) return false;
  if (user.scafferAccountKind === "operative") return false;
  const role = normalizeCompanyMemberRole(user.role ?? "");
  return role === "scaff";
}

export function isCompanyCrewSession(session: Session | null): boolean {
  if (!session?.user) return false;
  return isCompanyCrewUser(session.user as CrewUserLike);
}

/**
 * Roles that can open the office dashboard (and choose Office vs Field after sign-in).
 * Field-only `scaff` skips the chooser and goes straight to the crew app.
 */
export function hasOfficeWorkspaceAccess(role: string | null | undefined): boolean {
  const r = normalizeCompanyMemberRole(role ?? "");
  return r === "owner" || r === "admin" || r === "office" || r === "supervisor";
}

/**
 * Any company team member may open the field app.
 * Independent Scaff Pro operatives stay on /app/scaff.
 * Office roles pick Office vs Field after login; `scaff` goes to `/app/crew`.
 */
export function canAccessCompanyFieldApp(session: Session | null): boolean {
  if (!session?.user?.companyId) return false;
  if ((session.user as CrewUserLike).scafferAccountKind === "operative") return false;
  return true;
}

/** Paths crew may use without being redirected to crew home. */
export const CREW_ALLOWED_PATH_PREFIXES = [
  "/app/crew",
  "/app/my-work",
  "/app/notifications",
  "/app/inspections",
  "/app/compliance",
  "/app/job-sheets",
  "/app/site-visits",
  "/app/sites/",
  "/app/jobs/",
  "/app/labour/profile",
  "/app/scaff/preview",
  "/signout",
] as const;

export function isCrewAllowedAppPath(pathname: string): boolean {
  const path = pathname.split("?")[0]?.split("#")[0] ?? "";
  if (path === "/app/onboarding" || path === "/app/enter") return false;
  // Crew home is /app/crew — do not treat the office dashboard as an allowed escape.
  return CREW_ALLOWED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix)
  );
}

/** True when the path is a generic home (show workspace chooser for office roles). */
export function isGenericCompanyHomePath(pathname: string): boolean {
  const path = pathname.split("?")[0]?.split("#")[0] ?? "";
  return (
    path === "/app" ||
    path === "/app/" ||
    path === CREW_HOME_PATH ||
    path === `${CREW_HOME_PATH}/` ||
    path === WORKSPACE_CHOOSER_PATH ||
    path === `${WORKSPACE_CHOOSER_PATH}/`
  );
}

export function crewHomeWithClaimed(): string {
  return `${CREW_HOME_PATH}?claimed=1`;
}
